import axios from 'axios';
import type { TFunction } from 'i18next';

type AuthErrorScope = 'login' | 'register';

function errorKey(scope: AuthErrorScope, key: string): string {
  return scope === 'login' ? `auth.login.errors.${key}` : `auth.register.errors.${key}`;
}

/** Maps Firebase + API errors from Google/email auth flows to user-facing copy. */
export function getAuthFlowErrorMessage(err: unknown, t: TFunction, scope: AuthErrorScope): string {
  if (axios.isAxiosError(err)) {
    const apiCode =
      err.response?.data && typeof err.response.data === 'object' && 'code' in err.response.data
        ? String((err.response.data as { code: string }).code)
        : '';

    if (apiCode === 'DATABASE_UNAVAILABLE') {
      return t(errorKey(scope, 'databaseUnavailable'));
    }
    if (apiCode === 'FIREBASE_ADMIN_NOT_CONFIGURED') {
      return t(errorKey(scope, 'firebaseAdminMissing'));
    }
    if (err.response?.status === 401) {
      return t(errorKey(scope, 'backendAuthFailed'));
    }

    const axiosCode = err.code ?? '';
    if (axiosCode) {
      return t(errorKey(scope, 'googleFailedWithCode'), { code: axiosCode });
    }
  }

  const code =
    err && typeof err === 'object' && 'code' in err
      ? String((err as { code: string }).code)
      : '';

  if (code === 'auth/unauthorized-domain') {
    return t(errorKey(scope, 'unauthorizedDomain'));
  }
  if (code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request') {
    return t(errorKey(scope, 'googleCancelled'));
  }
  if (code) {
    return t(errorKey(scope, 'googleFailedWithCode'), { code });
  }

  return t(errorKey(scope, 'googleFailed'));
}
